/**
 * URWANA design tokens (the code and the API path stay `uruanna`).
 *
 * The brand (owner's decision, 2026-10-21): a deep indigo carries the mark and
 * every primary action; lavender is the mark's second burst and the decorative
 * tint; aqua is a *signal* — the focus ring, an unread dot, a live or loading
 * bar — and never text or a fill behind text. The light ground is a neutral
 * grey with white panels: no warm tint, no clay. The dark ground is black and
 * its grades.
 *
 * Nothing else in the app hard-codes a colour, radius or shadow.
 */

/** Indigo #240D54 — the mark, the light primary, the sign-in pane. */
export const indigo = {
  /** The brand colour itself: primary buttons (white label), the avatar. */
  brand: '#240D54',
  /** Pressed / hovered primary. */
  pressed: '#2E0C60',
  /** Text-sized indigo: links, the selected item's label, the info tone. */
  ink: '#3B1F86',
} as const;

/**
 * Lavender #9D82D7 — the mark's second burst and decoration in light mode; in
 * dark mode its lighter steps are the primary family.
 */
export const lavender = {
  500: '#9D82D7',
  /** Dark-mode primary (button fill, carries `#170A36` text). */
  300: '#B9A4EC',
  /** Dark-mode link / selected ink. */
  200: '#C9B8F2',
  /** Dark-mode link hover. */
  100: '#DCD0F7',
  /** Icon tiles on a white panel. */
  tint: '#F1EDFB',
} as const;

/**
 * Aqua #02E4C6 — a signal only: focus ring, unread dot, live/route bar.
 * Never text, never a fill behind text.
 */
export const aqua = { signal: '#02E4C6' } as const;

/** The light ground: neutral greys with white panels. */
export const neutral = {
  ground: '#F7F7F8',
  panel: '#FFFFFF',
  sunken: '#F1F1F3',
  /** Table header band. */
  head: '#FAFAFB',
  border: '#E6E6EA',
  borderStrong: '#D0D0D7',
  /** Selected / hovered row or nav item: a neutral fill, not a tint. */
  selected: '#EFEFF2',
} as const;

/**
 * The dark ground is black and its grades — a neutral grey scale with no warm
 * or cool cast (owner's call, 2026-09-17, after a navy and then a warm
 * charcoal were both rejected; kept by the 2026-10-21 rebrand). Panels sit one
 * clear step above the ground, the sunken well below it, and the only colour
 * comes from lavender, aqua and the status tones.
 */
export const graphite = {
  ground: '#0E0E10',
  panel: '#19191C',
  sunken: '#080809',
  raised: '#232326',
  head: '#151518',
  selected: '#26262A',
  border: '#2A2A2E',
  borderStrong: '#3A3A40',
} as const;

/**
 * One palette per mode. Every colour the interface uses is either one of
 * these or derived from one with `alpha()`. Text-on-surface pairs are AA
 * (>= 4.5:1) on `ground`, `panel` and `sunken`; `src/theme/theme.test.tsx`
 * fails if a token drifts below.
 */
export interface BrandPalette {
  /** The page. */
  ground: string;
  /** Panels, tables, drawers, menus. One step lighter than the ground. */
  panel: string;
  /** Wells inside a panel: toggle groups, code, the collapsed toolbar. */
  sunken: string;
  /** A table's header band. */
  tableHead: string;
  /** Hairlines. */
  border: string;
  /** Borders that must be seen: outlined buttons, hovered inputs. */
  borderStrong: string;
  ink: string;
  inkMuted: string;
  /** Meta text, breadcrumbs, section labels — still AA on every surface. */
  inkFaint: string;
  primary: string;
  primaryHover: string;
  onPrimary: string;
  /** Text-sized primary: links, the selected item's label, a focused label. */
  primaryInk: string;
  /** Lavender: decoration only (the mark's second burst, a marker). */
  accent: string;
  /** Lavender-family text, for the rare place decoration carries words. */
  accentInk: string;
  /** Background of an icon tile. */
  accentTint: string;
  /** Aqua: focus ring colour source, unread dot, live/loading bar. Never text. */
  signal: string;
  hover: string;
  selected: string;
  /** The keyboard focus ring (a box-shadow colour), aqua at partial alpha. */
  focusRing: string;
  disabledInk: string;
  disabledSurface: string;
  /**
   * A recessed cell inside a control: the segmented control's well, the
   * phone field's +964 prefix. Light it is `sunken`; dark it is one step
   * *above* the panel (#232326, dark pass), because the black `sunken` reads
   * as a hole in a control rather than a cell of it.
   */
  well: string;
  /** A disabled or locked field's fill (Components, Errors boards). */
  disabledField: string;
  /** The outline of a destructive outlined button. */
  dangerBorder: string;
  /** The approve act's fill and its label (review: Approve / Request changes / Reject). */
  approve: string;
  onApprove: string;
  /** A dashed drop zone's border. */
  dashed: string;
  /** Toasts and tooltips. */
  inverseSurface: string;
  inverseInk: string;
  /** A toast's inline action, as a link. */
  inverseAction: string;
  /** The inverse surface's edge: none on light, a hairline on black. */
  inverseBorder: string;
  /** The brand pane of the sign-in split: indigo in light, the panel in dark. */
  brandPane: string;
  scrim: string;
}

export const palettes: Record<'light' | 'dark', BrandPalette> = {
  light: {
    ground: neutral.ground,
    panel: neutral.panel,
    sunken: neutral.sunken,
    tableHead: neutral.head,
    border: neutral.border,
    borderStrong: neutral.borderStrong,
    ink: '#17132A',
    inkMuted: '#55526A',
    inkFaint: '#6B6880',
    primary: indigo.brand,
    primaryHover: indigo.pressed,
    onPrimary: '#FFFFFF',
    primaryInk: indigo.ink,
    accent: lavender[500],
    accentInk: indigo.ink,
    accentTint: lavender.tint,
    signal: aqua.signal,
    hover: neutral.selected,
    selected: neutral.selected,
    focusRing: 'rgba(2, 228, 198, 0.45)',
    disabledInk: '#8C8A9C',
    disabledSurface: '#ECECEF',
    well: neutral.sunken,
    disabledField: neutral.ground,
    dangerBorder: '#E8C7C4',
    approve: '#0A6B5E',
    onApprove: '#FFFFFF',
    dashed: '#C9C7D3',
    inverseSurface: '#17132A',
    inverseInk: '#FFFFFF',
    inverseAction: lavender[300],
    inverseBorder: 'transparent',
    brandPane: indigo.brand,
    scrim: 'rgba(23, 19, 42, 0.42)',
  },
  dark: {
    ground: graphite.ground,
    panel: graphite.panel,
    sunken: graphite.sunken,
    tableHead: graphite.head,
    border: graphite.border,
    borderStrong: graphite.borderStrong,
    ink: '#EDEDEF',
    inkMuted: '#B3B3B8',
    inkFaint: '#8C8C93',
    primary: lavender[300],
    primaryHover: lavender[200],
    onPrimary: '#170A36',
    primaryInk: lavender[200],
    accent: lavender[500],
    accentInk: lavender[200],
    accentTint: 'rgba(157, 130, 215, 0.16)',
    signal: aqua.signal,
    hover: 'rgba(255, 255, 255, 0.05)',
    selected: graphite.selected,
    focusRing: 'rgba(2, 228, 198, 0.35)',
    disabledInk: '#8C8C93',
    disabledSurface: graphite.raised,
    well: graphite.raised,
    disabledField: graphite.raised,
    dangerBorder: 'rgba(242, 145, 138, 0.45)',
    approve: '#4FE0CB',
    onApprove: '#06302A',
    dashed: graphite.borderStrong,
    inverseSurface: graphite.selected,
    inverseInk: '#EDEDEF',
    inverseAction: lavender[200],
    inverseBorder: graphite.borderStrong,
    brandPane: graphite.panel,
    scrim: 'rgba(0, 0, 0, 0.6)',
  },
};

/**
 * Status tones, one per meaning, as text. Each is AA as text on its mode's
 * panel and ground. `statusSurface` / `statusDot` are the chip's fill and dot.
 */
export const status = {
  neutral: { light: '#55526A', dark: '#B3B3B8' },
  pending: { light: '#7D5200', dark: '#F0B860' },
  active: { light: '#0A6B5E', dark: '#4FE0CB' },
  warning: { light: '#9C520B', dark: '#EDAA62' },
  danger: { light: '#B3261E', dark: '#F2918A' },
  info: { light: indigo.ink, dark: lavender[200] },
} as const;

/** A status chip's fill. Warning has no board value and is its ink at 9%/14%. */
export const statusSurface: Record<keyof typeof status, { light: string; dark: string }> = {
  neutral: { light: '#F1F1F3', dark: 'rgba(179, 179, 184, 0.14)' },
  pending: { light: '#FBF1DE', dark: 'rgba(240, 184, 96, 0.14)' },
  active: { light: '#E6F7F4', dark: 'rgba(79, 224, 203, 0.12)' },
  warning: { light: 'rgba(156, 82, 11, 0.09)', dark: 'rgba(237, 170, 98, 0.14)' },
  danger: { light: '#FBE9E7', dark: 'rgba(242, 145, 138, 0.14)' },
  info: { light: '#EEECF8', dark: 'rgba(185, 164, 236, 0.14)' },
};

/** A status chip's 7px dot. */
export const statusDot: Record<keyof typeof status, { light: string; dark: string }> = {
  neutral: { light: '#8C8A9C', dark: '#8C8C93' },
  pending: { light: '#D99A1E', dark: '#F0B860' },
  active: { light: '#02C4AA', dark: '#4FE0CB' },
  warning: { light: '#9C520B', dark: '#EDAA62' },
  danger: { light: '#D93A2F', dark: '#F2918A' },
  info: { light: lavender[500], dark: lavender[300] },
};

/**
 * An alert's four tones (Components and dark pass boards): the tone's fill, a
 * full 1px border in a lighter step of it — never a side stripe — the icon in
 * the tone, and the words. Light mode writes title and body in a deep step of
 * the tone; dark writes the title in the tone and the body in the ink, because
 * a pale tone over a long sentence on black tires the eye.
 */
export type AlertTone = 'success' | 'warning' | 'danger' | 'info';
export const alertTones: Record<
  AlertTone,
  Record<'light' | 'dark', { fill: string; border: string; icon: string; title: string; body: string }>
> = {
  success: {
    light: { fill: '#E6F7F4', border: '#C2EBE3', icon: '#0A6B5E', title: '#075247', body: '#075247' },
    dark: { fill: 'rgba(79, 224, 203, 0.12)', border: 'rgba(79, 224, 203, 0.28)', icon: '#4FE0CB', title: '#4FE0CB', body: '#EDEDEF' },
  },
  warning: {
    light: { fill: '#FBF1DE', border: '#F0DDB5', icon: '#7D5200', title: '#5C3D00', body: '#6B4A08' },
    dark: { fill: 'rgba(240, 184, 96, 0.14)', border: 'rgba(240, 184, 96, 0.30)', icon: '#F0B860', title: '#F0B860', body: '#EDEDEF' },
  },
  danger: {
    light: { fill: '#FBE9E7', border: '#F2CBC6', icon: '#B3261E', title: '#8C1D17', body: '#8C1D17' },
    dark: { fill: 'rgba(242, 145, 138, 0.14)', border: 'rgba(242, 145, 138, 0.30)', icon: '#F2918A', title: '#F2918A', body: '#EDEDEF' },
  },
  info: {
    light: { fill: '#EEECF8', border: '#DCD6F2', icon: indigo.ink, title: '#2A1566', body: '#2A1566' },
    dark: { fill: 'rgba(185, 164, 236, 0.14)', border: 'rgba(185, 164, 236, 0.30)', icon: lavender[200], title: lavender[200], body: '#EDEDEF' },
  },
};

/**
 * The patient's sex on the patients list, as a tinted chip (owner's call,
 * 2026-09-30): blue for male, pink for female. The one place colour carries a
 * meaning that is not a status, so it is its own pair and never borrowed from
 * `status` — an `info` tone would read as "something to know". Both are AA as
 * text on `panel` and `ground` in both modes, which `theme.test.tsx` holds.
 */
export const gender = {
  MALE: { light: '#1F5FA8', dark: '#8DB8EE' },
  FEMALE: { light: '#B0306E', dark: '#F09CC4' },
} as const;

/** The gender chip's fill (Components board): the tone's own pale step. */
export const genderSurface: Record<keyof typeof gender, { light: string; dark: string }> = {
  MALE: { light: '#E8F0FB', dark: 'rgba(141, 184, 238, 0.14)' },
  FEMALE: { light: '#FBE8F1', dark: 'rgba(240, 156, 196, 0.14)' },
};

/**
 * Controls 6px (a small button 5); chips, filter tags and menu items 8; panels, cards, menus,
 * alerts and toasts 10; dialogs and page shells 12; the empty-state tile is
 * `xl + 2`; `pill` only for count badges. `theme.shape.borderRadius` is `sm`,
 * so `borderRadius: 1` in `sx` is a control (6) and `2` a dialog (12).
 */
export const radii = { none: 0, xs: 4, smallControl: 5, sm: 6, md: 8, lg: 10, xl: 12, pill: 999 } as const;

export const spacingUnit = 8;

export const layout = {
  sidebarWidth: 248,
  sidebarCollapsedWidth: 68,
  topbarHeight: 64,
  /**
   * Wide enough to use a 1080p screen: at 1440 a list left ~120px of dead
   * margin either side while its own columns were cramped.
   */
  contentMaxWidth: 1760,
  /** A form drawer (Overlays board). Wider forms ask for `lg` explicitly. */
  drawerWidth: 460,
  /** The module rail as a temporary drawer below `lg` (Mobile board). */
  sidebarMobileWidth: 306,
  /** The aqua route bar under the top bar. */
  routeBarHeight: 3,
  gutter: 24,
} as const;

/** Shadows are tinted with the ink so they stay neutral on the grey ground. */
export const softShadows = {
  none: 'none',
  xs: '0 1px 2px rgba(23, 19, 42, 0.05)',
  sm: '0 1px 3px rgba(23, 19, 42, 0.06), 0 1px 2px rgba(23, 19, 42, 0.04)',
  md: '0 4px 12px rgba(23, 19, 42, 0.07)',
  popover: '0 6px 24px rgba(23, 19, 42, 0.12)',
  /** A toast: the menu shadow, a little heavier, because it floats over work. */
  toast: '0 6px 24px rgba(23, 19, 42, 0.18)',
  /** The chosen segment of a segmented control, lifted off the sunken well. */
  segment: '0 1px 2px rgba(23, 19, 42, 0.08)',
  overlay: '0 24px 60px rgba(23, 19, 42, 0.22)',
} as const;

/**
 * The dark mode's two lifted surfaces. An ink-tinted shadow is invisible on a
 * black ground, so menus and overlays darken the page behind them instead
 * (dark pass board).
 */
export const darkShadows = {
  popover: '0 6px 24px rgba(0, 0, 0, 0.4)',
  overlay: '0 24px 60px rgba(0, 0, 0, 0.5)',
} as const;

/**
 * There is no motion token, on purpose (owner, 2026-10-21): nothing in the
 * console moves except the logo family — `UruannaMark`'s `animate`, the boot
 * splash — the loading signals (the skeleton pulse, the aqua bar) and the page
 * enter (`PageEnter`). Their timings live with them, in
 * `components/brand/mark.css`, `components/layout/pageEnter.css` and
 * `index.html`.
 * The theme zeroes every MUI transition (`transitions` in `theme/index.ts`).
 */

export const fontFamilies = {
  /** Plex carries Latin and Arabic in one family, which the brand needs. */
  sans: "'IBM Plex Sans', 'IBM Plex Sans Arabic', system-ui, sans-serif",
  arabic: "'IBM Plex Sans Arabic', 'IBM Plex Sans', system-ui, sans-serif",
  /** Reserved for identifiers and tokens — not for labels. */
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;
